import { useState } from "react";
import { Link } from "react-router-dom";
import { Globe, Check, ArrowLeft } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSEO } from "@/lib/seo";
import { t } from "@/lib/i18n";
import { usePublicLang } from "@/lib/publicLang";

const COUNTRIES = [
  { value: "CA", label: "Canada" },
  { value: "GB", label: "United Kingdom" },
  { value: "AU", label: "Australia" },
  { value: "DE", label: "Germany" },
  { value: "FR", label: "France" },
  { value: "ES", label: "Spain" },
  { value: "IT", label: "Italy" },
  { value: "NL", label: "Netherlands" },
  { value: "SE", label: "Sweden" },
  { value: "NO", label: "Norway" },
  { value: "IE", label: "Ireland" },
  { value: "MX", label: "Mexico" },
  { value: "BR", label: "Brazil" },
  { value: "IN", label: "India" },
  { value: "JP", label: "Japan" },
  { value: "KR", label: "South Korea" },
  { value: "ZA", label: "South Africa" },
  { value: "OTHER", label: "Other / not listed" },
];

export default function Waitlist() {
  const [lang] = usePublicLang();
  useSEO({
    title: t(lang, "public.waitlist.title"),
    description: t(lang, "public.waitlist.subtitle"),
    path: "/waitlist",
  });

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [country, setCountry] = useState("CA");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      await api.post("/waitlist/join", {
        email: email.trim().toLowerCase(),
        name: name.trim(),
        country,
      });
      setDone(true);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-[color:var(--cream)]">
      <div className="brutal-card p-7 w-full max-w-md bg-[color:var(--paper)]" data-testid="waitlist-card">
        <Link to="/" className="pill-btn mb-5 inline-flex" data-testid="waitlist-back">
          <ArrowLeft size={14} /> {t(lang, "common.back")}
        </Link>

        {done ? (
          <div className="text-center" data-testid="waitlist-success">
            <div className="text-5xl mb-3" aria-hidden>✅</div>
            <h1 className="display text-2xl font-extrabold mb-2">{t(lang, "public.waitlist.success.title")}</h1>
            <p className="text-[color:var(--ink-soft)] leading-relaxed">
              {t(lang, "public.waitlist.success.body", { email })}
            </p>
            <Link to="/" className="pill-btn primary mt-5 w-full justify-center" data-testid="waitlist-success-home">
              {t(lang, "public.waitlist.success.home")}
            </Link>
          </div>
        ) : (
          <>
            <div className="brutal-card bg-[color:var(--cream)] p-4 mb-4" data-testid="waitlist-early-access-cta">
              <div className="display font-extrabold text-base mb-1">
                Early access is open. <span className="text-[color:var(--coral)]">Skip the waitlist.</span>
              </div>
              <p className="text-sm text-[color:var(--ink-soft)] mb-3">
                SonSpeak is in early access. Create your account and
                start building your child&apos;s board today.
              </p>
              <div className="flex flex-wrap gap-2">
                <Link
                  to="/register"
                  className="pill-btn primary"
                  data-testid="waitlist-register-cta"
                >
                  Create your account →
                </Link>
                <Link
                  to="/demo"
                  className="pill-btn"
                  data-testid="waitlist-demo-cta"
                >
                  Try the 60-second demo
                </Link>
              </div>
              <p className="text-[11px] text-[color:var(--ink-soft)] mt-3">
                Early access, no payment today. <Link to="/privacy-promise" className="underline">Read our privacy promise.</Link>
              </p>
            </div>
            <div className="text-center mb-5">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[color:var(--gold)] border-[3px] border-[color:var(--ink)] mb-3">
                <Globe size={26} />
              </div>
              <h1 className="display text-2xl font-extrabold mb-1">Rather wait? Get notified when your country goes live.</h1>
              <p className="text-[color:var(--ink-soft)] text-sm leading-relaxed">
                One email when we open in your country. That&apos;s it.
              </p>
            </div>

            <form onSubmit={submit} className="space-y-3">
              <label className="block">
                <span className="display font-semibold mb-1 block">{t(lang, "public.waitlist.name")}</span>
                <input
                  className="brutal-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  maxLength={80}
                  data-testid="waitlist-name"
                />
              </label>
              <label className="block">
                <span className="display font-semibold mb-1 block">{t(lang, "public.waitlist.email")}</span>
                <input
                  className="brutal-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  data-testid="waitlist-email"
                />
              </label>
              <label className="block">
                <span className="display font-semibold mb-1 block">{t(lang, "public.waitlist.country")}</span>
                <select
                  className="brutal-input"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  data-testid="waitlist-country"
                >
                  {COUNTRIES.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </label>

              {error && (
                <div className="brutal-card bg-rose-50 text-[color:var(--coral-deep)] p-3 text-sm" data-testid="waitlist-error">
                  {error}
                </div>
              )}

              <button
                type="submit"
                className="pill-btn primary w-full"
                disabled={busy}
                data-testid="waitlist-submit"
              >
                {busy ? t(lang, "public.waitlist.busy") : (
                  <>
                    <Check size={14} /> {t(lang, "public.waitlist.submit")}
                  </>
                )}
              </button>

              <p className="text-[11px] text-[color:var(--ink-soft)] text-center mt-3 leading-relaxed">
                {t(lang, "public.waitlist.privacy_note")}{" "}
                <Link to="/privacy" className="underline">/privacy</Link>.
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
